import * as React from "react";
import { Header, SpaceBetween, Spinner, Box, Button } from "@amzn/awsui-components-react";
import { Builder } from "builder-pattern";
import {
    AppError,
    EntityFactory,
    EntityPermissionOperationNames,
    EntityRef,
    EntityType,
    IEntityPermission,
    IMetrics,
    IPage,
    IPageDraft,
    Instance,
    InstanceFactory,
    Page,
    PageDraft,
    PageLibraryFactory
} from "@amzn/ask-legal-domain";
import { PageSectionV2 } from "../../components/page-section/PageSectionCompV2";
import { selectPageLiveDisplay } from "./page-live-display";
import { PageBaseLayoutV2 } from "../../components/page/PageBaseLayoutV2";
import { AppContext } from "../../setup/context";
import { Preference } from "../../setup/preference";
import { usePageTitle } from "../../contexts/TitleContext";
import { UIModel } from "../../model/ui-model";
import { FavorButtonV2 } from "../../components/common/FavorButtonV2";
import { PageDeprecated } from "../redirects/PageDeprecated";
import { LegalOnlyBadge } from "../../components/common/LegalOnlyTag";
import { PermissionDenied } from "../redirects/PermissionDenied";
import { PageNotFound } from "../redirects/PageNotFound";
import { CaptureAnalyticsComp } from "../../components/analytics/CaptureAnalyticsComp";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { callApi } from "../../utils/api-call";
import { PageContentEvaluation } from "../../components/page/PageContentEvaluation";
import { PageFeedbackModal } from "../../components/page/modal-content/PageFeedbackModal";
import { StarRating } from "../../components/common/StarRating";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { DraftConflictModal } from "../../components/common/DraftConflictModal";
import { TextHighlightShare } from "../../components/common/TextHighlightShare";
import { HighlightHandler } from "../../components/common/HighlightHandler";
import { FeedbackRating, IPageFeedback } from "@amzn/ask-legal-domain";
import { usePageTranslate } from "../../components/page/PageTranslateControl";
import "../../styles/component/layout/AskLegalLayout.scss";
import "../../styles/component/layout/PageLiveV2.scss";

type Loadable<T> =
    | { status: "loading" }
    | { status: "error"; err: AppError }
    | { status: "loaded"; data: T };

/**
 * Live page (and ?version=N history view) for the PathFinder Agentic UI lab.
 * Data loading, permissions, redirects, feedback, translation and highlighting
 * behave exactly as in PageLive; only the presentation changes. APIs are called directly
 * (the useAPI hooks are deprecated).
 */
export const PageLiveV2 = (props: {
    id: string;
    version?: number
}) => {
    const context = React.useContext(AppContext);
    const [pageState, setPageState] = React.useState<Loadable<IPage.LoadPageOutput>>({ status: "loading" });
    const [historyState, setHistoryState] = React.useState<Loadable<Page.Data>>({ status: "loading" });
    const [instance, setInstance] = React.useState<Instance>();
    const isFavoritedState = UIModel.State.use<boolean>({
        initialValue: false
    });

    const [hasEditorPermission, setHasEditorPermission] = React.useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
    const [pageRating, setPageRating] = React.useState<FeedbackRating | null>(null);
    // A 403 on the page triggers a public-info lookup that may redirect a deprecated page
    const [fallbackLoading, setFallbackLoading] = React.useState(false);

    // On-demand page translation UI (in-memory only; resets on reload/navigation).
    // Rendered only when both the Page Translation lab is enabled for the user AND
    // the instance has persisted pageTranslationEnabled = true (see `canTranslate` below).
    const pageTranslate = usePageTranslate(props.id);

    // History view: create a draft from this version, discarding an existing draft on conflict
    const [isCreatingDraft, setIsCreatingDraft] = React.useState(false);
    const [createDraftError, setCreateDraftError] = React.useState<AppError>();
    const [showConflictModal, setShowConflictModal] = React.useState(false);
    const [isDiscarding, setIsDiscarding] = React.useState(false);
    const [discardError, setDiscardError] = React.useState<AppError>();

    const isMountedRef = React.useRef<boolean>(true);
    // Only the latest page / history load / permission check may update state (props can change while loading)
    const latestPageLoadRef = React.useRef(0);
    const latestHistoryLoadRef = React.useRef(0);
    const latestPermissionCheckRef = React.useRef(0);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            latestPageLoadRef.current++;
            latestHistoryLoadRef.current++;
            latestPermissionCheckRef.current++;
        };
    }, []);

    const pageOutput = pageState.status === "loaded" ? pageState.data : undefined;
    const pageError = pageState.status === "error" ? pageState.err : undefined;
    const historyOutput = historyState.status === "loaded" ? historyState.data : undefined;
    const historyError = historyState.status === "error" ? historyState.err : undefined;

    // Stable reference: a new object on every render made PageContentEvaluation re-fetch the page each render
    const contentEvaluationPageRef = React.useMemo(() => ({
        id: props.id,
        version: props.version || 0,
        type: EntityType.Page
    }), [props.id, props.version]);

    const handleRatingChange = (newRating: FeedbackRating) => {
        setPageRating(newRating);

        if (instance && pageOutput) {
            setShowFeedbackModal(true);
        }
    };

    // Permission chain: instance admin, else page library editor, else page editor
    const checkEditorPermission = async (checkId: number) => {
        const isAuthorized = context.getEntityPermissionAPI().isAuthorized;
        const isAdmin = await callApi(isAuthorized, IEntityPermission.IsAuthorizedInput.create(
            InstanceFactory.fromEntityId(props.id),
            EntityType.Instance,
            EntityPermissionOperationNames.CAN_ADMIN
        ));
        if (checkId !== latestPermissionCheckRef.current) return;
        if (isAdmin.ok && isAdmin.data) {
            setHasEditorPermission(true);
            return;
        }
        if (!isAdmin.ok) {
            console.error("Failed to check admin permission, falling back to editor check");
        }
        const isLibraryEditor = await callApi(isAuthorized, IEntityPermission.IsAuthorizedInput.create(
            PageLibraryFactory.fromEntityId(props.id),
            EntityType.PageLibrary,
            EntityPermissionOperationNames.CAN_EDIT
        ));
        if (checkId !== latestPermissionCheckRef.current) return;
        if (isLibraryEditor.ok && isLibraryEditor.data) {
            setHasEditorPermission(true);
            return;
        }
        if (!isLibraryEditor.ok) {
            console.error("Failed to check PL editor permission, falling back to page editor check");
        }
        const isPageEditor = await callApi(isAuthorized, IEntityPermission.IsAuthorizedInput.create(
            props.id,
            EntityType.Page,
            EntityPermissionOperationNames.CAN_EDIT
        ));
        if (checkId !== latestPermissionCheckRef.current) return;
        if (isPageEditor.ok && isPageEditor.data) {
            setHasEditorPermission(true);
        } else if (!isPageEditor.ok) {
            console.error("Failed to check page editor permission");
        }
    };

    React.useEffect(() => {
        const checkId = ++latestPermissionCheckRef.current;
        setHasEditorPermission(false);
        checkEditorPermission(checkId);
    }, [props.id]);

    const pageTitle = React.useMemo(() => {
        if (!props.version && pageOutput) {
            return { title: pageOutput.page.title, withSuffix: false };
        }
        if (props.version && historyOutput) {
            return {
                title: `${historyOutput.title} (Version ${props.version})`,
                withSuffix: true
            };
        }
        return { title: undefined, withSuffix: false };
    }, [props.version, pageOutput, historyOutput]);

    usePageTitle(pageTitle.title, pageTitle.withSuffix);

    const loadExistingFeedback = async (page: IPage.LoadPageOutput["page"]) => {
        try {
            const identity = await context.getIdentity();
            const result = await callApi(
                context.getPageFeedbackAPI().load,
                IPageFeedback.LoadFeedbackInput.create({
                    pageRef: EntityFactory.toEntityRef(page),
                    userId: identity.id // Load feedback for current user only
                })
            );
            if (!isMountedRef.current) return;
            if (result.ok && result.data?.feedbacks?.length > 0) {
                setPageRating(result.data.feedbacks[0].rating);
            }
        } catch (error: unknown) {
            console.error("Failed to load existing feedback:", error);
        }
    };

    // Deprecated pages the user cannot view may still forward to their replacement
    const tryForwardDeprecatedPage = async () => {
        setFallbackLoading(true);
        const result = await callApi(context.getPageAPI().getPublicPageInfo, props.id);
        if (!isMountedRef.current) return;
        if (result.ok && result.data.deprecated && result.data.forwardingUrl) {
            window.location.assign(result.data.forwardingUrl);
        } else {
            setFallbackLoading(false);
        }
    };

    React.useEffect(() => {
        // Always load current page data (needed for draft ID construction in discard flow)
        const loadId = ++latestPageLoadRef.current;
        setPageState({ status: "loading" });
        callApi(context.getPageAPI().load2, props.id).then(result => {
            if (loadId !== latestPageLoadRef.current) return;
            if (!result.ok) {
                if (result.err.code === 403) {
                    tryForwardDeprecatedPage();
                }
                setPageState({ status: "error", err: result.err });
                return;
            }
            setPageState({ status: "loaded", data: result.data });
            isFavoritedState.setValue(result.data.isFavorited);
            callApi(
                context.getCommonAPI().recordViewHistory,
                IMetrics.RecordViewHistoryInput.create({
                    url: `/page/${props.id}/live`,
                    entityId: props.id,
                    type: "LivePage"
                })
            );
            loadExistingFeedback(result.data.page);
        });
        callApi(context.getInstanceAPI().load, InstanceFactory.fromEntityId(props.id)).then(result => {
            if (loadId !== latestPageLoadRef.current) return;
            setInstance(result.ok ? result.data : undefined);
        });
    }, [props.id]);

    React.useEffect(() => {
        if (props.version == null) return;
        const loadId = ++latestHistoryLoadRef.current;
        setHistoryState({ status: "loading" });
        callApi(
            context.getPageAPI().loadVersion,
            EntityFactory.fromEntityAttributes(props.id, EntityType.Page, props.version)
        ).then(result => {
            if (loadId !== latestHistoryLoadRef.current) return;
            setHistoryState(result.ok
                ? { status: "loaded", data: result.data }
                : { status: "error", err: result.err });
        });
    }, [props.id, props.version]);

    // Creates a draft from the viewed version, then opens the draft editor
    const createDraftFromVersion = async () => {
        if (props.version == null) return;
        setIsCreatingDraft(true);
        setCreateDraftError(undefined);
        const result = await callApi(
            context.getPageDraftAPI().createFromVersion,
            IPageDraft.CreateFromVersionInput.create({
                pageId: props.id,
                sourceVersion: props.version
            })
        );
        if (!isMountedRef.current) return;
        setIsCreatingDraft(false);
        if (result.ok) {
            window.location.assign(`/#/page/${props.id}/admin?tab=draftPage`);
        } else if (result.err.code === 409) {
            // A draft already exists
            setShowConflictModal(true);
        } else {
            setCreateDraftError(result.err);
        }
    };

    // Conflict modal: discard the existing draft, then create a new one from this version
    const discardDraftAndCreate = async () => {
        const currentVersion = pageOutput?.page?.version;
        if (currentVersion == null) return;
        const draftId = PageDraft.Factory.toPageDraftId(
            Builder<EntityRef>().id(props.id).version(currentVersion).build()
        );
        setIsDiscarding(true);
        setDiscardError(undefined);
        const result = await callApi(context.getPageDraftAPI().discard, draftId);
        if (!isMountedRef.current) return;
        setIsDiscarding(false);
        if (!result.ok) {
            // The error is shown in the modal until the user closes it
            setDiscardError(result.err);
            return;
        }
        setShowConflictModal(false);
        createDraftFromVersion();
    };

    const canTranslate = context.hasLab(Preference.Lab.PageTranslation) &&
        !!instance?.preferences?.features?.pageTranslationEnabled;

    const isRightSectionEmpty = (sections: { location: string; containers: unknown[] }[]) => {
        const rightSection = sections.find(s => s.location === "Right");
        return !!rightSection && rightSection.containers.length === 0;
    };

    const renderUpdatedOn = (output: IPage.LoadPageOutput) => {
        const { displayDate, authorIdentity } = selectPageLiveDisplay(output);
        return (
            <>
                Updated on {DateTimeFormatter.toSimpleDate(displayDate, 1)}
                {!!instance?.preferences?.features?.page?.showLastModifiedBy
                    && authorIdentity &&
                    (
                        <>
                            {" "}by <a
                                className="pf-live-author-link"
                                href={`https://phonetool.amazon.com/users/${authorIdentity.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {authorIdentity.name}
                            </a>
                        </>
                    )}
            </>
        );
    };

    const renderSections = (sections: Page.Section[], editor?: boolean) => {
        const mainSection = sections.find(s => s.location === "Main");
        const rightSection = sections.find(s => s.location === "Right");
        return (
            <PageBaseLayoutV2 expandable
                mainContent={mainSection ? <PageSectionV2.View section={mainSection} editor={editor} /> : <></>}
                rightContent={rightSection ? <PageSectionV2.View section={rightSection} editor={editor} /> : <></>}
                hideRightSection={isRightSectionEmpty(sections)}
            />
        );
    };

    const renderLiveHeader = (output: IPage.LoadPageOutput) => (
        <div className="pf-live-header">
            <SpaceBetween direction="vertical" size="s">
                {canTranslate && pageTranslate.banner}
                <Header
                    variant="h1"
                    description={renderUpdatedOn(output)}
                    actions={canTranslate ? pageTranslate.trigger : undefined}
                >
                    <span className="pf-live-title">
                        <span>{output.page.title}</span>
                        <FavorButtonV2
                            size="big"
                            entityLabel="page"
                            isFavoritedState={isFavoritedState}
                            targetEntityRef={EntityFactory.toEntityRef(output.page)}
                        />
                        <LegalOnlyBadge pageLibraryId={PageLibraryFactory.fromEntityId(props.id)}/>
                    </span>
                </Header>
                {context.hasLab(Preference.Lab.PageFeedback) &&
                    <div className="pf-live-feedback">
                        <Box variant="span" fontWeight="bold">
                            How useful was this page:
                        </Box>
                        <StarRating
                            value={pageRating}
                            onChange={handleRatingChange}
                            size="medium"
                        />
                        {instance && (
                            <PageFeedbackModal
                                pageId={props.id}
                                instanceId={instance.id}
                                visible={showFeedbackModal}
                                onDismiss={() => setShowFeedbackModal(false)}
                                pageRef={EntityFactory.toEntityRef(output.page)}
                                selectedRating={pageRating}
                            />
                        )}
                    </div>
                }
                <PageContentEvaluation pageRef={contentEvaluationPageRef} />
            </SpaceBetween>
        </div>
    );

    if (!props.version) {
        // Render live page
        return (
            <div className="pf-live-page">
                {pageOutput && !pageOutput.page.deprecated && renderLiveHeader(pageOutput)}
                {pageState.status === "loading" &&
                    <div className="pf-live-status">
                        <Spinner size="large"/> Locating Page Resources...
                    </div>
                }
                {pageError && (
                    pageError.code === 403 ?
                        (fallbackLoading ? <Spinner size="large"/> :
                         <PermissionDenied entityId={props.id} accessType="LivePage"/>)
                        : <PageNotFound/>
                )}
                {pageOutput && (
                    pageOutput.page.deprecated ?
                        <PageDeprecated
                            pageOwner={pageOutput.page.pageOwner}
                            forwardingUrl={pageOutput.page.forwardingUrl}
                        /> :
                        <CaptureAnalyticsComp
                            entityRef={EntityFactory.toEntityRef(pageOutput.page)}
                        >
                            <>
                                {renderSections(pageOutput.page.sections, hasEditorPermission)}
                                <TextHighlightShare />
                                <HighlightHandler showNotFoundMessage={true} />
                            </>
                        </CaptureAnalyticsComp>
                )}
            </div>
        );
    }
    // Render history page for specific version

    return (
        <div className="pf-live-page">
            <div className="pf-live-header">
                {historyOutput ? (
                    <Header
                        variant="h1"
                        actions={hasEditorPermission && (
                            <Button
                                variant="primary"
                                loading={isCreatingDraft}
                                onClick={createDraftFromVersion}>
                                Create Draft from this Version
                            </Button>
                        )}
                    >
                        {historyOutput.title} (Version {props.version})
                    </Header>
                ) : (
                    <Header variant="h1">{props.id.split("-")[2]}</Header>
                )}
            </div>
            {createDraftError && <ErrorFlashbar error={createDraftError}/>}
            {historyState.status === "loading" && <Spinner />}
            {historyError && (
                historyError.code === 403 ?
                    <PermissionDenied entityId={props.id} accessType="LivePage"/> : <PageNotFound/>
            )}
            {historyOutput && renderSections(historyOutput.sections)}
            {/* Conflict modal: draft already exists for this page */}
            {showConflictModal && (
                <DraftConflictModal
                    onContinueEditing={() => {
                        setShowConflictModal(false);
                        window.location.assign(`/#/page/${props.id}/admin?tab=draftPage`);
                    }}
                    onDiscardAndCreate={discardDraftAndCreate}
                    isDiscarding={isDiscarding}
                    discardError={discardError}
                    onCancel={() => {
                        setDiscardError(undefined);
                        setShowConflictModal(false);
                    }}
                />
            )}
        </div>
    );
};
